import { useMemo, useState } from 'react';
import { Input } from '../../Input/Input';
import { Select } from '../../Select/Select';
import type { SelectOption } from '../../Select/Select.types';
import { WithholdingFormDialog } from './WithholdingFormDialog';
import { useWithholdingsTranslation } from './i18n';
import {
  useWithholdingClasses,
  useWithholdingKeys,
  useWithholdingOperationCenters,
} from './hooks';
import type { CreateWithholdingPayload, WithholdingsCatalogs, WithholdingKeyOption } from './types';

export interface WithholdingsAddModalProps {
  open: boolean;
  /** Loaders de catálogo (clase/clave/centro). */
  catalogs: WithholdingsCatalogs;
  /** Mutación en vuelo. */
  isSubmitting: boolean;
  onSubmit: (payload: CreateWithholdingPayload) => void;
  onClose: () => void;
}

/**
 * Modal Agregar retención manual (FEAT-105-11.02).
 *
 * Captura clase/clave/centro (vía `Select` de catálogo) y base/valor. NO expone
 * `withholdingAction`: el backend lo calcula desde su matriz de decisión (GOTCHA
 * documentado en la spec §2.3). El centro de operación se exige solo si la clave
 * seleccionada es de regionalidad municipal.
 */
export function WithholdingsAddModal({
  open,
  catalogs,
  isSubmitting,
  onSubmit,
  onClose,
}: WithholdingsAddModalProps) {
  const { t } = useWithholdingsTranslation();

  const [classId, setClassId] = useState('');
  const [keyId, setKeyId] = useState('');
  const [operationCenterId, setOperationCenterId] = useState('');
  const [base, setBase] = useState('');
  const [total, setTotal] = useState('');
  const [touched, setTouched] = useState(false);

  const classesQuery = useWithholdingClasses({ catalogs, enabled: open });
  const keysQuery = useWithholdingKeys({ catalogs, classId: classId || null, enabled: open });
  const ocQuery = useWithholdingOperationCenters({ catalogs, enabled: open });

  // Cualquiera de los tres catálogos que falle deja el formulario inservible:
  // se avisa una sola vez en lugar de dejar los selects vacíos en silencio.
  const catalogsFailed = classesQuery.isError || keysQuery.isError || ocQuery.isError;

  const classOptions: SelectOption[] = useMemo(
    () => (classesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [classesQuery.data],
  );
  const keyOptions: SelectOption[] = useMemo(
    () =>
      (keysQuery.data ?? []).map((k) => ({
        value: k.id,
        label: k.code ? `${k.code} — ${k.name}` : k.name,
      })),
    [keysQuery.data],
  );
  const ocOptions: SelectOption[] = useMemo(
    () => (ocQuery.data ?? []).map((o) => ({ value: o.id, label: o.name })),
    [ocQuery.data],
  );

  const selectedKey: WithholdingKeyOption | undefined = useMemo(
    () => (keysQuery.data ?? []).find((k) => k.id === keyId),
    [keysQuery.data, keyId],
  );
  const ocRequired = selectedKey?.regionality === 'municipal';

  const baseNum = Number(base);
  const totalNum = Number(total);
  const baseValid = base.trim() !== '' && !Number.isNaN(baseNum) && baseNum > 0;
  const totalValid = total.trim() !== '' && !Number.isNaN(totalNum) && totalNum > 0;
  const ocValid = !ocRequired || Boolean(operationCenterId);
  const formValid = Boolean(classId) && Boolean(keyId) && baseValid && totalValid && ocValid;

  function reset() {
    setClassId('');
    setKeyId('');
    setOperationCenterId('');
    setBase('');
    setTotal('');
    setTouched(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleSubmit() {
    setTouched(true);
    if (!formValid) return;
    onSubmit({
      withholdingClassId: classId,
      withholdingKeyId: keyId,
      operationCenterId: ocRequired ? operationCenterId : null,
      baseAmount: baseNum,
      totalValue: totalNum,
    });
  }

  return (
    <WithholdingFormDialog
      open={open}
      title={t('withholdings.add.title')}
      submitLabel={t('withholdings.add.submit')}
      cancelLabel={t('withholdings.add.cancel')}
      isSubmitting={isSubmitting}
      submitDisabled={!formValid}
      onSubmit={handleSubmit}
      onClose={handleClose}
    >
      {/* Los tres catálogos se cargaban sin mirar `isError`: si el backend fallaba,
          el usuario veía selects vacíos y sin explicación. `withholdings.error.catalog`
          existía en el catálogo pero no estaba conectada a nada. */}
      {catalogsFailed && (
        <p
          role="alert"
          className="mb-3 rounded-md bg-error-bg px-3 py-2 text-sm text-error-content"
        >
          {t('withholdings.error.catalog')}
        </p>
      )}
      <Select
        label={t('withholdings.add.class')}
        placeholder={t('withholdings.add.classPlaceholder')}
        options={classOptions}
        value={classId}
        required
        error={touched && !classId}
        errorMessage={touched && !classId ? t('withholdings.validation.required') : undefined}
        onChange={(v) => {
          setClassId(String(v));
          // Cambiar la clase invalida la clave y el centro seleccionados.
          setKeyId('');
          setOperationCenterId('');
        }}
      />

      <Select
        label={t('withholdings.add.key')}
        placeholder={t('withholdings.add.keyPlaceholder')}
        options={keyOptions}
        value={keyId}
        required
        disabled={!classId || keysQuery.isLoading}
        error={touched && !keyId}
        errorMessage={touched && !keyId ? t('withholdings.validation.required') : undefined}
        onChange={(v) => {
          setKeyId(String(v));
          setOperationCenterId('');
        }}
      />

      {ocRequired && (
        <Select
          label={t('withholdings.add.operationCenter')}
          placeholder={t('withholdings.add.operationCenterPlaceholder')}
          options={ocOptions}
          value={operationCenterId}
          required
          error={touched && !operationCenterId}
          errorMessage={
            touched && !operationCenterId
              ? t('withholdings.validation.operationCenterRequired')
              : undefined
          }
          onChange={(v) => setOperationCenterId(String(v))}
        />
      )}

      <Input
        label={t('withholdings.add.base')}
        type="number"
        min={0}
        step="any"
        inputMode="decimal"
        value={base}
        error={touched && !baseValid}
        errorMessage={touched && !baseValid ? t('withholdings.validation.basePositive') : undefined}
        onChange={(e) => setBase(e.target.value)}
      />

      <Input
        label={t('withholdings.add.total')}
        type="number"
        min={0}
        step="any"
        inputMode="decimal"
        value={total}
        error={touched && !totalValid}
        errorMessage={touched && !totalValid ? t('withholdings.validation.totalPositive') : undefined}
        onChange={(e) => setTotal(e.target.value)}
      />
    </WithholdingFormDialog>
  );
}
